import {
  createContext,
  useContext,
  useState,
  useCallback,
  useEffect,
  type ReactNode
} from 'react'
import { useLocation } from 'react-router-dom'
import { useAuth } from '@/modules/auth'
import { UserProfileApi } from '@/shared/api/users'

const THEME_STORAGE_KEY = 'sitrepp-theme'

export type ThemePreference = 'light' | 'dark' | 'system'

type ResolvedTheme = 'light' | 'dark'
const LIGHT_ONLY_PUBLIC_PATHS = ['/', '/about', '/docs', '/terms', '/privacy', '/contact']

interface ThemeContextValue {
  theme: ThemePreference
  setTheme: (value: ThemePreference) => void
  resolvedTheme: ResolvedTheme
}

const ThemeContext = createContext<ThemeContextValue | null>(null)

function getSystemTheme(): ResolvedTheme {
  if (typeof window === 'undefined') return 'light'
  return window.matchMedia('(prefers-color-scheme: dark)').matches ? 'dark' : 'light'
}

function resolveTheme(theme: ThemePreference): ResolvedTheme {
  if (theme === 'system') return getSystemTheme()
  return theme
}

function applyTheme(resolved: ResolvedTheme) {
  const root = document.documentElement
  if (resolved === 'dark') {
    root.classList.add('dark')
  } else {
    root.classList.remove('dark')
  }
}

/** Matches sidebar logo in dark mode (white) vs light (brand color); uses #app-favicon in index.html */
function syncAppFavicon(resolved: ResolvedTheme): void {
  const link = document.getElementById('app-favicon') as HTMLLinkElement | null
  if (!link) return
  if (resolved === 'dark') {
    link.href = '/favicon-dark.svg'
    link.type = 'image/svg+xml'
  } else {
    link.href = '/favicon-128.png'
    link.type = 'image/png'
  }
}

export function isLightOnlyPublicRoute(pathname: string): boolean {
  return LIGHT_ONLY_PUBLIC_PATHS.includes(pathname)
}

interface ThemeProviderProps {
  children: ReactNode
  /** When provided (e.g. from profile), syncs theme from server and persists changes via onThemeChange */
  initialTheme?: ThemePreference | null
  forcedResolvedTheme?: ResolvedTheme | null
  userId?: string | null
  onThemeChange?: (theme: ThemePreference) => Promise<void>
}

export function ThemeProvider({
  children,
  initialTheme = null,
  forcedResolvedTheme = null,
  userId = null,
  onThemeChange
}: ThemeProviderProps) {
  const [theme, setThemeState] = useState<ThemePreference>(() => {
    if (typeof window === 'undefined') return 'light'
    const stored = localStorage.getItem(THEME_STORAGE_KEY) as ThemePreference | null
    if (stored && ['light', 'dark', 'system'].includes(stored)) return stored
    return 'light'
  })

  const [resolvedTheme, setResolvedTheme] = useState<ResolvedTheme>(() =>
    resolveTheme(theme)
  )

  // Sync from server when initialTheme becomes available
  useEffect(() => {
    if (initialTheme && ['light', 'dark', 'system'].includes(initialTheme)) {
      setThemeState(initialTheme as ThemePreference)
    }
  }, [initialTheme])

  // Apply theme to document whenever preference, route override, or system preference changes.
  useEffect(() => {
    const nextResolvedTheme = forcedResolvedTheme ?? resolveTheme(theme)
    setResolvedTheme(nextResolvedTheme)
    applyTheme(nextResolvedTheme)
    syncAppFavicon(nextResolvedTheme)
  }, [theme, forcedResolvedTheme])

  // Listen to system preference when theme is 'system'
  useEffect(() => {
    if (theme !== 'system' || forcedResolvedTheme) return
    const mql = window.matchMedia('(prefers-color-scheme: dark)')
    const handler = () => {
      const resolved = getSystemTheme()
      setResolvedTheme(resolved)
      applyTheme(resolved)
      syncAppFavicon(resolved)
    }
    mql.addEventListener('change', handler)
    return () => mql.removeEventListener('change', handler)
  }, [theme, forcedResolvedTheme])

  const setTheme = useCallback(
    (value: ThemePreference) => {
      setThemeState(value)
      try {
        localStorage.setItem(THEME_STORAGE_KEY, value)
      } catch {
        // ignore
      }
      if (userId && onThemeChange) {
        onThemeChange(value).catch(() => {
          // Optionally toast or log; state already updated locally
        })
      }
    },
    [userId, onThemeChange]
  )

  const value: ThemeContextValue = {
    theme,
    setTheme,
    resolvedTheme
  }

  return (
    <ThemeContext.Provider value={value}>
      {children}
    </ThemeContext.Provider>
  )
}

export function useTheme(): ThemeContextValue {
  const context = useContext(ThemeContext)
  if (!context) {
    throw new Error('useTheme must be used within ThemeProvider')
  }
  return context
}

/** Wrapper that fetches theme from user profile and persists changes via API. Use inside AuthProvider. */
export function ThemeProviderWrapper({ children }: { children: ReactNode }) {
  const [profileTheme, setProfileTheme] = useState<ThemePreference | null>(null)
  const { user, getAccessToken } = useAuth()
  const location = useLocation()
  const forcedResolvedTheme = isLightOnlyPublicRoute(location.pathname)
    ? 'light'
    : null

  useEffect(() => {
    if (!user?.id || !getAccessToken) return
    const api = new UserProfileApi(getAccessToken)
    api
      .getUserProfile(user.id)
      .then((profile) => {
        if (profile.theme_preference) {
          setProfileTheme(profile.theme_preference)
        }
      })
      .catch(() => {})
  }, [user?.id, getAccessToken])

  const onThemeChange = useCallback(
    async (theme: ThemePreference) => {
      if (!user?.id || !getAccessToken) return
      const api = new UserProfileApi(getAccessToken)
      await api.updateUserProfile(user.id, { theme_preference: theme })
    },
    [user?.id, getAccessToken]
  )

  return (
    <ThemeProvider
      initialTheme={profileTheme}
      forcedResolvedTheme={forcedResolvedTheme}
      userId={user?.id ?? null}
      onThemeChange={onThemeChange}
    >
      {children}
    </ThemeProvider>
  )
}
